import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageSquare, Award, Sparkles, Target, Play, ChevronDown } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import MarketingNav from '@/components/marketing/MarketingNav';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import HowItWorks from '@/components/marketing/HowItWorks';
import FeaturesShowcase from '@/components/marketing/FeaturesShowcase';
import Testimonials from '@/components/marketing/Testimonials';
import FAQ from '@/components/marketing/FAQ';
import CTASection from '@/components/marketing/CTASection';
import AppShowcase from '@/components/marketing/AppShowcase';
import LogoMarquee from '@/components/marketing/LogoMarquee';

import RoleplayShowcase from '@/components/marketing/RoleplayShowcase';
import FeedbackShowcase from '@/components/marketing/FeedbackShowcase';
import RotatingWord from '@/components/marketing/RotatingWord';

import { useEffect, useState, useRef, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import Seo from '@/components/Seo';

const landingFaqs = [
  { q: 'What is Sellaris?', a: 'Sellaris is an AI-powered sales training platform designed for collegiate sales competition preparation, with realistic AI buyer roleplay and instant rubric-based feedback.' },
  { q: 'How does the AI buyer work?', a: 'AI buyers are trained on real sales competition scripts and respond dynamically to your questions, simulating realistic objections and natural conversation. You can practice via voice or text.' },
  { q: 'What scoring criteria do you use?', a: 'We use the standard collegiate sales competition rubric with 7 categories: Open, Needs Identification, Solution Presentation, Overcoming Objections, Close, Communication, and Overall Performance.' },
  { q: 'Can I practice with voice?', a: 'Yes — Sellaris supports full voice conversation mode using advanced speech-to-text, and the AI buyer responds with synthesized voice.' },
  
  { q: 'What competitions does Sellaris prepare me for?', a: 'Sellaris prepares you for major collegiate sales competitions including NCSC, ICSC, and regional events.' },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: landingFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const isMobile = useIsMobile();
  const rafRef = useRef<number>();

  // Throttled scroll handler using RAF - disabled on mobile for performance
  useEffect(() => {
    if (isMobile) return; // Disable parallax on mobile
    
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafRef.current = undefined;
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile]);

  const handleStart = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Seo
        title="Sellaris | Sales Enablement Platform"
        description="Practice AI sales roleplays with instant rubric feedback. Built for competitions, programs, and enterprise teams."
        path="/"
        jsonLd={faqJsonLd}
      />
      {/* Ambient background — one soft brand wash, no competing orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-grid opacity-[0.02]" />
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[520px] bg-primary/10 rounded-full blur-[120px] will-change-transform"
          style={!isMobile ? { transform: `translate(-50%, ${scrollY * 0.06}px)` } : { transform: 'translateX(-50%)' }}
        />
        <div
          className="absolute bottom-0 right-[-10%] w-[520px] h-[520px] bg-accent/[0.07] rounded-full blur-[140px]"
        />
      </div>


      <MarketingNav />

      <main>
      {/* Hero Section - Enhanced */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 min-h-[86vh] flex items-center">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center justify-center text-center max-w-5xl mx-auto">
            <div className="space-y-6 sm:space-y-8 w-full">
              {/* Eyebrow */}
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-subtle bg-card/60 backdrop-blur-sm animate-fade-in"
                style={{ animationDelay: '0.1s' }}
              >
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <span className="text-[11px] sm:text-xs font-medium tracking-[0.06em] text-muted-foreground">
                  Objective Intelligence Sales Training
                </span>
              </div>

              {/* Main title */}
              <h1
                className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight animate-fade-in leading-[1.02] text-balance"
                style={{ animationDelay: '0.2s' }}
              >
                <span className="block">Enable your</span>
                <span className="block text-primary">
                  <RotatingWord words={["classroom", "enterprise"]} />
                </span>

              </h1>

              {/* Subtitle */}
              <p
                className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed animate-fade-in"
                style={{ animationDelay: '0.4s' }}
              >
                With the most customizable Sales Enablement Platform
              </p>

              {/* CTAs — one primary, one secondary, one quiet */}
              <div
                className="pt-2 sm:pt-4 animate-fade-in flex flex-col sm:flex-row gap-3 justify-center items-center"
                style={{ animationDelay: '0.5s' }}
              >
                <Button
                  size="lg"
                  onClick={handleStart}
                  aria-label="Start practicing sales for free"
                  className="group w-full sm:w-auto"
                >
                  <Play className="w-4 h-4" />
                  Start practicing
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/try')}
                  aria-label="Try Sellaris yourself with a free demo workspace"
                  className="w-full sm:w-auto"
                >
                  Try it yourself
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => navigate('/contact')}
                  aria-label="Book a demo"
                  className="group text-muted-foreground hover:text-foreground w-full sm:w-auto"
                >
                  Book a demo
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </div>

              {/* Proof strip */}
              <div
                className="animate-fade-in flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-2 text-xs sm:text-sm text-muted-foreground"
                style={{ animationDelay: '0.6s' }}
              >
                <span>Built for higher ed &amp; enterprise</span>
                <span className="hidden sm:inline text-border">•</span>
                <span>Voice, video, and text roleplay</span>
                <span className="hidden sm:inline text-border">•</span>
                <span>Feedback in under a minute</span>
              </div>


            </div>

            {/* Feature Cards with stagger animation */}
            <div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-16 sm:mt-24 w-full px-2 sm:px-0" 
            >
              <FeatureCard 
                icon={<MessageSquare className="w-5 h-5" />}
                title="AI buyers that see you"
                description="Buyers respond to your words, tone, and body language — pushing back on weak discovery, rewarding strong questions."
                delay={0.7}
              />
              <FeatureCard 
                icon={<Target className="w-5 h-5" />}
                title="Multi-category scoring"
                description="Scored on Open, Needs, Value, Objections, Close, Communication and Impact, with SPIN and CAC breakdowns."
                delay={0.8}
              />
              <FeatureCard 
                icon={<Award className="w-5 h-5" />}
                title="Progress analytics"
                description="Trend charts, category breakdowns, streak tracking, and AI coaching recommendations over time."
                delay={0.9}
              />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <ChevronDown className="w-5 h-5 text-muted-foreground/50 animate-bounce" />
        </div>
      </section>

      <LogoMarquee />


      <div>
        <AppShowcase />
      </div>

      <div>
        <HowItWorks />
      </div>

      <div>
        <RoleplayShowcase />
      </div>

      <div>
        <FeedbackShowcase />
      </div>

      <div>
        <FeaturesShowcase />
      </div>

      <div>
        {/* <Testimonials /> */}
      </div>

      <div>
        <FAQ />
      </div>

      <div>
        <CTASection />
      </div>
      </main>

      <div>
        <MarketingFooter />
      </div>
    </div>
  );
};

const FeatureCard = ({
  icon,
  title,
  description,
  delay = 0,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`group rounded-2xl border border-subtle bg-card/60 backdrop-blur-sm p-5 sm:p-6 text-left transition-all duration-300 hover:border-border hover:-translate-y-0.5 hover:shadow-elevation-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="w-9 h-9 rounded-lg border border-subtle bg-muted/40 text-primary flex items-center justify-center mb-4 transition-colors duration-200 group-hover:border-primary/30">
        {icon}
      </div>
      <p className="font-display text-lg mb-1.5 text-foreground" role="heading" aria-level={3}>{title}</p>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
};

export default Landing;
